import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";

export const Hero = () => {
  const mediaRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: mediaRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.18]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 18);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 12);
  };

  return (
    <section id="top" className="relative pt-16">
      <div
        data-testid="ops-wall-hero"
        className="relative h-[calc(100vh-4rem)] border-b border-zinc-800 overflow-hidden"
      >
        <iframe
          data-testid="ops-wall-hero-frame"
          src="/equinox-ops-wall.html?embed=1"
          sandbox="allow-scripts allow-same-origin"
          title="Project Equinox Operations Wall — live orbital-to-subsea data chain"
          className="w-full h-full border-0 block"
        />
      </div>

      <h1 data-testid="hero-headline" className="sr-only">
        Project Equinox — Autonomous 10 MW Off-Grid Enclosure Tranches
      </h1>

      <div
        ref={mediaRef}
        data-testid="hero-media-block"
        onMouseMove={onMouseMove}
        onMouseLeave={() => {
          mx.set(0);
          my.set(0);
        }}
        className="relative w-full h-[62vh] sm:h-[78vh] overflow-hidden border-y border-zinc-800"
      >
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <div className="kenburns absolute inset-0">
          <motion.img
            src="/hero-enclosure.jpg"
            alt="Fenced high-desert compound of Equinox autonomous off-grid data enclosures with solar arrays, wind turbines and satellite tracking dishes"
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
            className="w-full h-full object-cover"
            style={{ x: sx, y: sy, filter: "brightness(0.92)" }}
          />
          </div>
        </motion.div>
        <div className="absolute inset-0 hero-grid-overlay pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/40 pointer-events-none" />

        <div className="absolute top-5 left-5 text-[9px] tracking-[0.3em] uppercase text-zinc-300/80">
          Node 01 / Active Enclosure Tranche
        </div>
        <div className="absolute top-5 right-5 text-[9px] tracking-[0.3em] uppercase text-zinc-300/80 text-right hidden sm:block">
          Site Coordinates — Restricted
        </div>
        <div className="absolute bottom-5 left-5 text-[9px] tracking-[0.3em] uppercase text-zinc-300/80">
          10 MW Captive Microgrid / Liquid-Cooled
        </div>
        <div className="absolute bottom-5 right-5 text-[9px] tracking-[0.3em] uppercase text-zinc-300/80 text-right hidden sm:block">
          Orbital Uplink: Locked
        </div>
      </div>

      <div
        data-testid="regulatory-banner-eo14373"
        className="border-b border-zinc-800 bg-[#0A0A0A]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-start gap-4">
          <span className="status-dot mt-1 inline-block w-1.5 h-1.5 shrink-0 rounded-full bg-green-500" />
          <p className="text-[10px] sm:text-[11px] leading-relaxed tracking-[0.08em] uppercase text-zinc-500">
            Operations governed under the precise cross-border capitalization and
            virtualization protections of U.S. Executive Order 14373. Primary asset
            clearings and entity allocations routed via active U.S. Trustee escrow
            structures.
          </p>
        </div>
      </div>
    </section>
  );
};
